import { useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate, Navigate, useLocation } from "react-router";
import { AppointmentForm } from "../appointment-form/appointment-form";
import { BookingDetails } from "./booking-details";
import { useCreateAppointment } from "../../api/update-appointment";
import { useAvailableAppointments } from "@/api/get-available-appointments";
import { format } from "date-fns";
import { formatDate } from "@/utils/format/date-time";
import { useAuth } from "@/hooks/use-auth";

const schema = yup
    .object({
        date: yup.string().required("Please select a date"),
        time: yup.string().required("Please select a time"),
        confirmContact: yup
            .boolean()
            .oneOf([true], "Please select this option")
            .required(),
        confirmationType: yup
            .array()
            .min(1, "Please select one option")
            .max(1, "Please select one option"),
    })
    .required();

export const BookAppointment = () => {
    const { claims, loading } = useAuth();

    // Book clinician
    const { state } = useLocation();

    const clinician = state?.clinician;
    const service = state?.service;
    const params = state?.params;

    const searchParams = new URLSearchParams(state?.params ?? "");

    // Start on the month containing the requested start date,
    // or fall back to the current month when no start date is provided
    const [activeMonth, setActiveMonth] = useState(() =>
        searchParams.get("startDate")
            ? format(new Date(searchParams.get("startDate")), "yyyyMM")
            : format(new Date(), "yyyyMM"),
    );

    const { data: availableDates = [], isLoading } = useAvailableAppointments(
        clinician?.userID,
        activeMonth,
        service?.serviceGuid,
        claims === "patient",
        loading,
    );

    const startDate = (() => {
        const searchStartDate = searchParams.get("startDate");
        const searchEndDate = searchParams.get("endDate");

        // Find the first available date within the requested date range
        const availableDate = availableDates.find(({ startDate }) => {
            const date = formatDate(startDate);

            return (
                date >= formatDate(searchStartDate) &&
                date <= formatDate(searchEndDate)
            );
        });

        // Use the first available date within the requested range,
        // or fall back to the first available date if none are in range
        return availableDate?.startDate
            ? formatDate(availableDate.startDate)
            : formatDate(availableDates[0]?.startDate) || null;
    })();

    // Set react hook form default values
    const defaultValues = availableDates
        ? {
              date: startDate,
              time: null,
              confirmationType: [],
          }
        : undefined;

    const methods = useForm({
        resolver: yupResolver(schema),
        values: defaultValues,
    });

    const navigate = useNavigate();

    const onCancel = () => {
        navigate(
            `/clinicians/view/${clinician.firstName}-${clinician.lastName}/${clinician.userID}`,
            {
                state: { params },
            },
        );
    };

    // Submit
    const mutation = useCreateAppointment();

    const onSubmit = (data) => {
        mutation.mutate({ data, clinician, service });
    };

    if (claims !== "patient") {
        return (
            <p>
                Clinicians cannot book appointments at this time. Please log in
                as a patient to book an appointment.
            </p>
        );
    }

    if (!clinician || !service) {
        return <Navigate to="/clinicians/find" replace />;
    }

    return (
        <div className="relative container mx-auto flex flex-1 flex-col gap-x-8 gap-y-4 md:flex-row">
            <div className="xl:3/12 relative w-full lg:w-4/12">
                <BookingDetails clinician={clinician} service={service} />
            </div>
            <div className="xl:9/12 w-full lg:w-8/12">
                <AppointmentForm
                    mode="create"
                    context={service}
                    methods={methods}
                    onCancel={onCancel}
                    onSubmit={onSubmit}
                    startDate={startDate}
                    availableDates={availableDates}
                    onMonthChange={setActiveMonth}
                    isPending={mutation.isPending || mutation.isSuccess}
                    isLoading={isLoading}
                />
            </div>
        </div>
    );
};
